import React, { useEffect, useState } from 'react';

/**
 * Full-screen brand splash shown once per browser session.
 *
 * Behaviour:
 *   • First page load in a session → mount, fade in the lotus logo,
 *     hold for ~2.4s, fade out (600ms), then unmount and reveal the app.
 *   • sessionStorage flag prevents flashing on client-side route changes
 *     inside the same tab session.
 *   • Respects `prefers-reduced-motion` by skipping the splash entirely.
 */
const SESSION_KEY = 'pou_splash_shown_v1';
const HOLD_MS = 6000;
const FADE_MS = 700;

export default function SplashScreen() {
  const [visible, setVisible] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      if (sessionStorage.getItem(SESSION_KEY) === '1') return false;
      const rm = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (rm) {
        sessionStorage.setItem(SESSION_KEY, '1');
        return false;
      }
    } catch { /* ignore */ }
    return true;
  });
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (!visible) return undefined;
    const holdTimer = setTimeout(() => setLeaving(true), HOLD_MS);
    const exitTimer = setTimeout(() => {
      try { sessionStorage.setItem(SESSION_KEY, '1'); } catch { /* ignore */ }
      setVisible(false);
    }, HOLD_MS + FADE_MS);
    return () => {
      clearTimeout(holdTimer);
      clearTimeout(exitTimer);
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Loading Plus Ultra Innovation — ProcureOne Ultra"
      className={`pou-splash ${leaving ? 'pou-splash--leaving' : ''}`}
      data-testid="splash-screen"
    >
      <div className="pou-splash__inner">
        <img
          src="/plus-ultra-logo.webp"
          alt="Plus Ultra Innovation"
          className="pou-splash__logo"
          data-testid="splash-logo-img"
        />
        <div className="pou-splash__tag">
          <span className="pou-splash__tag-line">PLUS ULTRA INNOVATION PRESENTS</span>
          <span className="pou-splash__tag-product">
            <span className="pou-splash__tag-white">ProcureOne</span>{' '}
            <span className="pou-splash__tag-gold">Ultra</span>
          </span>
        </div>
        <div className="pou-splash__bar" aria-hidden="true">
          <span />
        </div>
      </div>
      <style>{`
        .pou-splash {
          position: fixed;
          inset: 0;
          z-index: 2147483647;
          display: flex;
          align-items: center;
          justify-content: center;
          background: radial-gradient(circle at 50% 40%, #FFFDF6 0%, #F4EFDF 55%, #EAE0BE 100%);
          animation: pou-splash-in 380ms ease-out both;
        }
        .pou-splash--leaving {
          animation: pou-splash-out ${FADE_MS}ms ease-in forwards;
        }
        .pou-splash__inner {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 20px;
          padding: 24px;
          max-width: min(88vw, 520px);
        }
        .pou-splash__logo {
          width: min(62vw, 360px);
          height: auto;
          border-radius: 50%;
          background: #ffffff;
          box-shadow:
            0 0 0 1px rgba(212, 175, 55, 0.35),
            0 0 80px -10px rgba(212, 175, 55, 0.55),
            0 30px 60px -20px rgba(10, 31, 68, 0.35);
          animation: pou-splash-logo 1200ms ease-out both;
        }
        .pou-splash__tag {
          text-align: center;
          font-family: 'Inter', system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
          animation: pou-splash-tag 900ms ease-out 250ms both;
        }
        .pou-splash__tag-line {
          display: block;
          font-size: 11px;
          letter-spacing: 0.28em;
          color: #7A5A16;
          font-weight: 600;
          margin-bottom: 6px;
        }
        .pou-splash__tag-product {
          display: block;
          font-size: clamp(20px, 4.4vw, 32px);
          font-weight: 900;
          letter-spacing: -0.01em;
          line-height: 1;
        }
        .pou-splash__tag-white { color: #041B36; }
        .pou-splash__tag-gold { color: #B8912A; }
        .pou-splash__bar {
          width: min(60vw, 220px);
          height: 3px;
          background: rgba(10, 31, 68, 0.10);
          border-radius: 999px;
          overflow: hidden;
          margin-top: 6px;
        }
        .pou-splash__bar > span {
          display: block;
          height: 100%;
          width: 40%;
          background: linear-gradient(90deg, transparent, #D4AF37, transparent);
          transform: translateX(-100%);
          animation: pou-splash-bar 1400ms cubic-bezier(0.4, 0, 0.2, 1) infinite;
        }
        @keyframes pou-splash-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes pou-splash-out {
          from { opacity: 1; }
          to   { opacity: 0; visibility: hidden; }
        }
        @keyframes pou-splash-logo {
          0%   { transform: scale(0.82); opacity: 0; filter: blur(6px); }
          60%  { transform: scale(1.04); opacity: 1; filter: blur(0); }
          100% { transform: scale(1);    opacity: 1; }
        }
        @keyframes pou-splash-tag {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes pou-splash-bar {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(260%); }
        }
      `}</style>
    </div>
  );
}
